/* revisionSections.js — Shared mapping between revision_requests[] items
 * and the plan editor's section ids. Used by:
 *
 *   - PlanEditorInline.jsx   → renders an inline RevisionInlineBanner
 *     above each section that has at least one open revision item.
 *   - PlannerCaseDetail.jsx  → builds the "X of Y addressed" progress
 *     counter for the sticky resubmit footer.
 *   - RevisionRequestPanel.jsx → slim summary card on the Doctor's
 *     submission tab.
 *   - PlannerResponseHero.jsx → renders per-row decision badges in the
 *     doctor's response card on the Treatment Plan tab.
 *
 * Keeping the mapping in one place ensures the planner-side highlight,
 * the progress count, and the doctor-side decision badges can never
 * disagree about which row belongs to which section.
 *
 * iter98.89 — extracted from RevisionRequestPanel + extended to cover
 * free-text categories (mapped to the section they most naturally
 * belong to, with a "plan_viewer" pseudo-section for top-of-editor
 * comments on the whole plan).
 */

/* Structured numeric/chip fields → which plan editor section they
 * belong to. Mirrors the keys the doctor's RequestRevisionPage uses
 * when it builds the structured_changes payload. */
export const STRUCT_FIELDS = {
    aligner_count_upper:     { label: "Upper aligners",     unit: "aligners", section: "wear",         planKey: "aligner_count_upper" },
    aligner_count_lower:     { label: "Lower aligners",     unit: "aligners", section: "wear",         planKey: "aligner_count_lower" },
    duration_weeks:          { label: "Duration",           unit: "weeks",    section: "wear",         planKey: "estimated_duration_weeks" },
    wear_days:               { label: "Wear schedule",      unit: "days",     section: "wear",         planKey: "change_interval_days" },
    overjet_initial_mm:      { label: "Overjet (initial)",  unit: "mm",       section: "bite_metrics", planKey: "initial_overjet_mm" },
    overbite_initial_mm:     { label: "Overbite (initial)", unit: "mm",       section: "bite_metrics", planKey: "initial_overbite_mm" },
    final_overjet_mm:        { label: "Final overjet",      unit: "mm",       section: "bite_metrics", planKey: "final_overjet_mm" },
    final_overbite_mm:       { label: "Final overbite",     unit: "mm",       section: "bite_metrics", planKey: "final_overbite_mm" },
    total_ipr_mm:            { label: "Total IPR",          unit: "mm",       section: "bite_metrics", planKey: "total_ipr_mm" },
    retainer_type:           { label: "Retainer type",      unit: "",         section: "retention",    planKey: "retainer_type" },
    follow_up_recall_months: { label: "Follow-up recall",   unit: "months",   section: "retention",    planKey: "retention_recall_months" },
    wear_protocol_text:      { label: "Wear protocol",      unit: "",         section: "retention",    planKey: "retention_schedule" },
};

/* Free-text item categories → which plan editor section they belong
 * to. `viewer_comment` is special: the doctor's plan-viewer comment is
 * about the WHOLE plan, so we surface it at the top of the deliverable
 * section (the section that owns the plan-viewer link). */
export const CATEGORY_TO_SECTION = {
    // Legacy free-form categories
    ipr:                     "protocol",
    attachments:             "pertooth",
    sequence:                "pertooth",
    aligner_count:           "wear",
    torque_tip:              "pertooth",
    extractions:             "pertooth",
    retention:               "retention",
    other:                   "deliverable",
    // iter97.52 categories from the new full-page Request a Revision flow
    viewer_comment:          "deliverable",
    attachments_revision:    "pertooth",
    ipr_revision:            "protocol",
    elastics_revision:       "protocol",
    rationale_revision:      "protocol",
    overcorrection_revision: "protocol",
    overall_notes:           "deliverable",
    // iter98.99bf — per-tooth + wear-schedule categories from the
    // restructured Request a Revision page (one section per Treatment-
    // Plan tab).
    per_tooth_movements_revision:   "pertooth",
    per_tooth_attachments_revision: "pertooth",
    per_tooth_cuts_revision:        "pertooth",
    per_tooth_biteramps_revision:   "pertooth",
    wear_schedule_notes:            "wear",
};

export const CATEGORY_LABELS = {
    ipr:                     "IPR",
    attachments:             "Attachments",
    sequence:                "Movement sequence",
    aligner_count:           "Aligner count",
    torque_tip:              "Torque / Tip / Rotation",
    extractions:             "Extraction plan",
    retention:               "Retention protocol",
    other:                   "Other",
    viewer_comment:          "Plan viewer comment",
    attachments_revision:    "Attachments",
    ipr_revision:            "IPR",
    elastics_revision:       "Elastics",
    rationale_revision:      "Rationale / notes",
    overcorrection_revision: "Overcorrection",
    overall_notes:           "Overall notes",
    per_tooth_movements_revision:   "Per-tooth movements",
    per_tooth_attachments_revision: "Per-tooth attachments",
    per_tooth_cuts_revision:        "Per-tooth precision cuts",
    per_tooth_biteramps_revision:   "Per-tooth bite ramps",
    wear_schedule_notes:            "Wear schedule notes",
};

/* Friendly labels for the section ids so banners can read e.g.
 * "Doctor's revision · Aligners & wear schedule". */
export const SECTION_LABELS = {
    bite_metrics: "Bite metrics",
    pertooth:     "Per-tooth plan",
    protocol:     "Proposed protocol",
    wear:         "Aligners & wear schedule",
    retention:    "Retention plan",
    deliverable:  "3rd-party deliverable",
};

/* Expand a revision_request into a flat array of inline rows.
 *
 * Each row has:
 *   key       — stable id (matches what gets sent in `decisions[].key`)
 *   kind      — "struct" or "free"
 *   section   — section_id from the plan editor (used to group)
 *   label     — human-readable row label
 *   from/to   — present on struct rows (current vs requested value)
 *   unit      — present on struct rows
 *   planKey   — present on struct rows; the editor field to set when accepted
 *   note      — present on free rows (the doctor's typed note)
 *   severity  — present on free rows (critical / normal / minor)
 *   teeth     — present on free rows
 *   category  — present on free rows (the original category id)
 */
export function expandRevisionItems(rev, currentPlan = {}) {
    if (!rev) return [];
    const out = [];
    const struct = rev.structured_changes || {};
    Object.entries(struct).forEach(([field, value]) => {
        if (value === null || value === undefined || value === "") return;
        const meta = STRUCT_FIELDS[field];
        if (!meta) return;
        const fromVal = currentPlan[meta.planKey];
        out.push({
            key:     `struct.${field}`,
            kind:    "struct",
            section: meta.section,
            label:   meta.label,
            unit:    meta.unit,
            planKey: meta.planKey,
            from:    fromVal,
            to:      value,
        });
    });
    (rev.items || []).forEach((it) => {
        const section = CATEGORY_TO_SECTION[it.category] || "deliverable";
        out.push({
            key:      `item.${it.category}`,
            kind:     "free",
            section,
            label:    CATEGORY_LABELS[it.category] || it.category || "Note",
            note:     it.note || "",
            severity: it.severity || "normal",
            teeth:    it.teeth || [],
            category: it.category,
        });
    });
    return out;
}

/* Group an expanded row list by section_id. Returns
 * { bite_metrics: [...], wear: [...], ... } with only the sections
 * that actually have items. */
export function groupBySection(rows) {
    const out = {};
    rows.forEach((r) => {
        if (!out[r.section]) out[r.section] = [];
        out[r.section].push(r);
    });
    return out;
}

/* Pick the most recent revision_request from a case's array. Returns
 * null when the array is empty. Used by the planner workspace + the
 * doctor's review page. */
export function latestRevision(revisions) {
    if (!revisions?.length) return null;
    return [...revisions].sort((a, b) => (b.iteration || 0) - (a.iteration || 0))[0];
}

/* True when the planner still has at least one un-decided row on the
 * latest open revision_request. */
export function hasPendingDecisions(rows, decisions) {
    return rows.some((r) => !decisions?.[r.key]);
}
